import React, { useState, useEffect } from "react";
import api from "../services/api";
import DomainFields, { mergeCompetitors } from "./DomainFields";
import { MAX_COMPETITORS, toHostname } from "../utils/targetInputs";
import useProjectStore from "../store/useProjectStore";
import Spinner from "../shared/shared-component/Spinner";

// Must match PROJECT_CONTEXT_MAX_LENGTH in backend/models/Project.js
const PROJECT_CONTEXT_MAX_LENGTH = 2000;
const GLOBAL_LOCATION = "Global";

const LABEL = {
  fontSize: "11px",
  fontWeight: "600",
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  display: "block",
  marginBottom: "6px",
};

const INPUT = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border)",
  background: "rgba(255,255,255,0.04)",
  color: "var(--text-heading)",
  fontSize: "13px",
  outline: "none",
};

const HINT = {
  fontSize: "12px",
  color: "var(--text-muted)",
  lineHeight: "1.6",
  margin: "0 0 8px",
};

function Step({ index, title, description }) {
  return (
    <div className=" d-flex gap__12 flex-start">
      <span
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          flexShrink: 0,
          background: "rgba(6,182,212,0.12)",
          border: "1px solid rgba(6,182,212,0.3)",
          color: "var(--teal-bright)",
          fontSize: "11px",
          fontWeight: "700",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {index}
      </span>
      <div>
        <div className="fb__13 fc__0E1218 fw__600">
          {title}
        </div>
        <div className="fb__12 fc__5F636A mt__2">
          {description}
        </div>
      </div>
    </div>
  );
}

/**
 * Blocking first-run screen.
 *
 * Two modes, both of which end with the user owning at least one project that
 * has a main domain:
 *   • create   — the user has no projects at all
 *   • complete — the user has a project that predates domains and must fill
 *                its main domain in before the app will run anything
 *
 * `onDone` is called once the account is in a usable state; App re-reads the
 * project list and drops the gate.
 */
export default function OnboardingPage({
  incompleteProject = null,
  userName = "",
  showAdminLink = false,
  onDone,
  onLogout,
}) {
  const isCompleteMode = !!incompleteProject;

  const [name, setName] = useState(incompleteProject?.name || "");
  const [domain, setDomain] = useState("");
  const [competitors, setCompetitors] = useState(["", ""]);
  const [context, setContext] = useState(incompleteProject?.context || "");
  const [targetLocation, setTargetLocation] = useState(
    incompleteProject?.targetLocation || "United States",
  );
  const [locationOptions, setLocationOptions] = useState(["United States"]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getProjectLocations()
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.locations)) {
          setLocationOptions(res.data.locations);
        }
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCompetitorChange = (index, value) => {
    setCompetitors((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setErrors((prev) => ({ ...prev, [`competitor${index}`]: "" }));
  };

  // Client-side pre-flight. The server re-validates everything.
  const validate = () => {
    const next = {};
    const host = toHostname(domain);
    if (!domain.trim()) {
      next.domain = "Main domain is required";
    } else if (!host.includes(".")) {
      next.domain = "Enter a valid domain, e.g. acme.com";
    }

    const savedRivals = incompleteProject?.competitors || [];
    const seen = new Set([host, ...savedRivals].filter(Boolean));
    competitors.forEach((c, i) => {
      if (savedRivals[i] || !c.trim()) return;
      const ch = toHostname(c);
      if (!ch.includes(".")) {
        next[`competitor${i}`] = "Enter a valid domain, e.g. rival.com";
      } else if (seen.has(ch)) {
        next[`competitor${i}`] =
          ch === host
            ? "A competitor cannot be your main domain"
            : "This domain is already listed";
      } else {
        seen.add(ch);
      }
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...(name.trim() ? { name: name.trim() } : {}),
        domain: domain.trim(),
        competitors: mergeCompetitors(
          incompleteProject?.competitors || [],
          competitors,
        ),
        context: context.trim(),
        targetLocation,
      };
      if (isCompleteMode) {
        await api.updateProject(incompleteProject.id, payload);
      } else {
        await api.createProject(payload);
      }
      // update the store, so if it fails, log it and carry on.
      try {
        const res = await api.getProjects();
        useProjectStore.getState().setProjects(res.data.projects || []);
      } catch (refreshErr) {
        console.error("Store refresh failed:", refreshErr);
      }

      onDone();
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // What the project will be called if the name is left blank — mirrors the
  // server's fallback so the placeholder is a real preview, not a guess.
  const derivedName = incompleteProject?.domain || toHostname(domain);

  const greeting = userName ? `, ${userName.split(" ")[0]}` : "";

  return (
    <div className="wrapper__onBoarding__page wrapper__main">
      <div className="wrapper__container__1010">
        {/* Header */}
        <div className="wrapper__head mb__28">
          <div className="steps__circle mb__20">
            {isCompleteMode ? "Finish setup" : "Step 1 of 1"}
          </div>
          <div className="wrapper__heading mb__20">
            <h1 className="h2 fc__0E1218 mb__16 fw__700">{isCompleteMode
              ? `Finish setting up "${incompleteProject.name}"`
              : `Welcome${greeting} — set up your project`}
            </h1>
            <p className="fb__16 fc__5F636A">{isCompleteMode
              ? "This project was created before domains were required. Add its main domain to carry on running agents."
              : "Every analysis runs inside a project, so we need to know which site you are working on before you can launch an agent."}</p>
          </div>
        </div>

        <div className="onboarding__grid custom__row">
          <div className="onboarding__grid__left">
            <div className="wrapper__onbaording__form">
              <form onSubmit={handleSubmit}>
                {submitError && (
                  <div
                    className="alert alert-error"
                    style={{ marginBottom: "18px" }}
                  >
                    <span>⚠</span> {submitError}
                  </div>
                )}

                <DomainFields
                  savedDomain={incompleteProject?.domain || null}
                  savedCompetitors={incompleteProject?.competitors || []}
                  domain={domain}
                  competitors={competitors}
                  onDomainChange={(v) => {
                    setDomain(v);
                    setErrors((p) => ({ ...p, domain: "" }));
                  }}
                  onCompetitorChange={handleCompetitorChange}
                  disabled={saving}
                  errors={errors}
                  autoFocus
                />

                <div className="wrapper__sec  mb__20">
                  <label className="form__label">
                    Project Name{" "}
                    <span className="fc__5F6567 fb__11 transform__none">
                      (optional)
                    </span>
                  </label>
                  <p className="fb__hint">
                    Leave this blank and the project is named after its main domain.
                    It is only a label for your own reference — rename it any time.
                  </p>
                  <input
                    className="form__input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={derivedName || "e.g. Acme — Main Site"}
                    maxLength={80}
                    disabled={saving}
                  />
                </div>

                <div className="wrapper__sec  mb__20">
                  <label className="form__label">Target Location</label>
                  <p className="fb__hint">
                    The market audits run for — SERP checks, rankings, keyword
                    volumes and AI-answer visibility all reflect this country. You
                    can change this later.
                  </p>
                  <select
                   className="form__input form__select"
                    value={targetLocation}
                    onChange={(e) => setTargetLocation(e.target.value)}
                    disabled={saving}

                  >
                    {(locationOptions.includes(targetLocation)
                      ? locationOptions
                      : [targetLocation, ...locationOptions]
                    ).map((loc) => (
                      <option key={loc} value={loc}>
                        {loc === GLOBAL_LOCATION
                          ? `${loc} (no specific market)`
                          : loc}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wrapper__sec  mb__20">
                  <label className="form__label">Project Context (optional)</label>
                  <p className="fb__hint">
                    Describe the business, audience, locations and tech stack so
                    results are specific instead of guessed from the URL. Editable
                    later.
                  </p>
                  <p
                    style={{ ...HINT, color: "var(--teal-bright)", opacity: 0.85 }}
                  >
                    Applies to your main domain only — it is not sent when an agent
                    is run against a competitor.
                  </p>
                  <textarea
                    className="form__input"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    maxLength={PROJECT_CONTEXT_MAX_LENGTH}
                    disabled={saving}
                    placeholder={
                      "e.g. B2B SaaS for logistics teams in the US and Canada. " +
                      "Next.js, client-side rendered. Priority pages: /pricing, /platform."
                    }
                  />
                  <div className="fb__11 mt__5 fc__5F636A text__right">
                    {context.length}/{PROJECT_CONTEXT_MAX_LENGTH}
                  </div>
                </div>
                <div className="form__submit d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap__12">
                    <button
                      type="button"
                      onClick={onLogout}
                      disabled={saving}
                      className="common__btn common__blk__btn"
                    >
                      Sign out
                    </button>
                    {showAdminLink && (
                      <a href="/admin/users" className="fb__11 fw__500 fc__5F636A fc__ECAA0B">
                        Admin Panel →
                      </a>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="common__btn common__org__btn"
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner size = "small" text="Saving..." />
                    ) : isCompleteMode ? (
                      "Save & Continue →"
                    ) : (
                      "Create Project & Continue →"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="onboarding__grid__right">
            <aside>
              <h3 className="fb__13 fw__500 mb__10 fc__0E1218 uppercase">
                What happens next
              </h3>
              <Step
                index="1"
                title="Your domains are locked in"
                description={`Enter your main domain and up to ${MAX_COMPETITORS} competitor domains to run the agents against.`}
              />
              <Step
                index="2"
                title="Pick a target"
                description="Choose one of those domains to run the agent against. Add a page path if you want to analyze a specific page."
              />
              <Step
                index="3"
                title="Competitors can wait"
                description="Leave them blank now and add them from project settings whenever you are ready."
              />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
